import React, { useEffect, useState, useRef, useCallback } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";

import UserAddressService from "../../services/useraddress/useraddress.service";
import { selectUserAddress } from "../../redux/slices/user-address";
import ProductStorageHelpers from "../../utils/product-local-storage-wrapper";
import { UserAddress, UserEditAddress1 } from "../../models/user-address-model";
import GetAddressByLocation from "../user-address/location-search/get-location";
import TokenService from "../../services/autentication/token.service";
import { useCustomerDelivery } from "../../providers/CustomerDeliveryProvider";
import { checkCurrentCheckoutDelivery } from "../../services/products/checkout-delivery.service";

// --- Form Component (Internal) ---
interface AddressFormProps {
    addressId?: number;
    onSuccess: () => void;
    onCancel: () => void;
}

const AddressForm: React.FC<AddressFormProps> = ({ addressId, onSuccess, onCancel }) => {
    const { t } = useTranslation();
    const isEdit = !!addressId;
    const [loading, setLoading] = useState(false);
    const [areas, setAreas] = useState<any[]>([]);
    const [pincodeData, setPincodeData] = useState<any>(null);
    const [isMapVisible, setIsMapVisible] = useState(false);

    const user = TokenService.getUser();
    const selectedAdminUserId = useAppSelector(state => state.adminUser.selectedAdminUserId);
    const userId = selectedAdminUserId || user?.userID;

    const formik = useFormik({
        initialValues: {
            id: addressId || 0,
            addressLine1: "",
            addressLine2: "",
            landMark: "",
            pincode: "",
            areaId: "",
            gpsLocation: "",
            userName: user?.name || ""
        },
        validate: (values) => {
            const errors: any = {};
            if (!values.addressLine1) errors.addressLine1 = "Required";
            if (!values.pincode) errors.pincode = "Required";
            else if (values.pincode.length !== 6) errors.pincode = "Must be 6 digits";
            if (!values.areaId) errors.areaId = "Required";
            return errors;
        },
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const data: any = {
                    userAddressID: values.id,
                    address1: values.addressLine1,
                    address2: values.addressLine2,
                    landmark: values.landMark,
                    zipcode: values.pincode,
                    gpsLocation: values.gpsLocation || null,
                    userID: userId,
                    areaId: Number(values.areaId),
                    userName: values.userName
                };

                const res = isEdit ? await UserAddressService.editUserAddress(data) : await UserAddressService.apiuserAddress1(data);
                if (res && res.isSuccess) {
                    onSuccess();
                } else {
                    console.error("Address save failed:", res);
                    // Usually Toast would be passed from parent or used via ref, 
                    // but for now we'll rely on the parent's reload or an alert if needed.
                    alert(res?.message || "Failed to save address. Please check all fields.");
                }
            } catch (error: any) {
                console.error("Address save error:", error);
                alert("An error occurred while saving address.");
            } finally {
                setLoading(false);
            }
        }
    });

    useEffect(() => {
        if (isEdit && addressId) {
            const fetchPromise = selectedAdminUserId
                ? UserAddressService.getUserAddressById(selectedAdminUserId)
                : UserAddressService.getUserAddress(0);

            fetchPromise.then((list: any) => {
                const addr = list.find((a: any) => a.userAddressID === addressId);
                if (addr) {
                    formik.setValues({
                        id: addr.userAddressID,
                        addressLine1: addr.address1,
                        addressLine2: addr.address2,
                        landMark: addr.landmark,
                        pincode: addr.zipcode,
                        areaId: addr.areaId,
                        gpsLocation: addr.gpsLocation,
                        userName: addr.userName || user?.name || ""
                    });
                    fetchPincode(addr.zipcode, addr.areaId);
                }
            });
        }
    }, [addressId, selectedAdminUserId, user?.name]);

    const fetchPincode = async (val?: string, currentAreaId?: any) => {
        const pc = val || formik.values.pincode;
        if (pc && pc.length === 6 && !isNaN(Number(pc))) {
            setLoading(true);
            try {
                const res = await UserAddressService.getAddress(Number(pc));
                if (res && res.isSuccess) {
                    const fetchedAreas = res.areas || [];
                    // Defensive mapping for area property names
                    const normalizedAreas = fetchedAreas.map((a: any) => ({
                        areaId: a.areaId || a.areaID,
                        areaName: a.areaName
                    }));
                    setAreas(normalizedAreas);
                    setPincodeData(res);
                    
                    if (normalizedAreas.length === 1) {
                        const singleId = normalizedAreas[0].areaId;
                        formik.setFieldValue("areaId", singleId);
                    } else if (currentAreaId) {
                        formik.setFieldValue("areaId", currentAreaId);
                    }
                } else {
                    setAreas([]);
                    setPincodeData(null);
                    if (res && res.message) alert(res.message);
                }
            } catch (err) {
                console.error("fetchPincode error:", err);
                setAreas([]);
            } finally {
                setLoading(false);
            }
        }
    };

    const handlePincodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        formik.setFieldValue("pincode", val);
        if (val.length !== 6) {
            setAreas([]);
            formik.setFieldValue("areaId", "");
            setPincodeData(null);
        } else {
            fetchPincode(val);
        }
    };

    return (
        <form onSubmit={formik.handleSubmit} className="space-y-4 animate-in">
            <div className="field">
                <label className="grey-label">Recipient Name*</label>
                <InputText name="userName" value={formik.values.userName} onChange={formik.handleChange} className={`w-full ${formik.touched.userName && formik.errors.userName ? 'p-invalid' : ''}`} placeholder="Name for this address" />
                {formik.touched.userName && formik.errors.userName && <small className="p-error">{formik.errors.userName as string}</small>}
            </div>
            <div className="field">
                <label className="grey-label">Address Line 1*</label>
                <InputTextarea rows={2} name="addressLine1" value={formik.values.addressLine1} onChange={formik.handleChange} className={`w-full ${formik.touched.addressLine1 && formik.errors.addressLine1 ? 'p-invalid' : ''}`} />
                {formik.touched.addressLine1 && formik.errors.addressLine1 && <small className="p-error">{formik.errors.addressLine1 as string}</small>}
            </div>
            <div className="field">
                <label className="grey-label">Address Line 2</label>
                <InputTextarea rows={2} name="addressLine2" value={formik.values.addressLine2} onChange={formik.handleChange} className="w-full" />
            </div>
            <div className="grid">
                <div className="col-12">
                    <label className="grey-label">Pincode*</label>
                    <div className="p-inputgroup">
                        <InputText name="pincode" value={formik.values.pincode} onChange={handlePincodeChange} className={formik.touched.pincode && formik.errors.pincode ? 'p-invalid' : ''} maxLength={6} />
                        <Button type="button" icon="pi pi-search" className="checkout-btn" onClick={() => fetchPincode()} loading={loading} />
                    </div>
                    {formik.touched.pincode && formik.errors.pincode && <small className="p-error">{formik.errors.pincode as string}</small>}
                </div>
                <div className="col-12 mt-2">
                    <label className="grey-label">Area*</label>
                    <Dropdown 
                        value={areas.length === 1 ? areas[0].areaId : formik.values.areaId} 
                        options={areas} 
                        optionLabel="areaName"
                        optionValue="areaId"
                        onChange={(e) => formik.setFieldValue("areaId", e.value)}
                        placeholder="Select Area"
                        className={`w-full ${formik.touched.areaId && formik.errors.areaId ? 'p-invalid' : ''}`}
                    />
                    {formik.touched.areaId && formik.errors.areaId && <small className="p-error">{formik.errors.areaId as string}</small>}
                </div>
            </div>
            
            {pincodeData && (
                <div className="grid text-sm text-gray-500 bg-gray-50 p-2 rounded">
                    <div className="col-6">City: {pincodeData.districts?.[0]?.districtName}</div>
                    <div className="col-6">State: {pincodeData.states?.[0]?.stateName}</div>
                </div>
            )}

            <Button type="button" label="Set Map Location" icon="pi pi-map-marker" outlined className="w-full p-button-secondary" onClick={() => setIsMapVisible(true)} />

            <div className="flex gap-3 mt-6">
                <Button type="button" label="Cancel" outlined className="flex-1" onClick={onCancel} />
                <Button type="submit" label={isEdit ? "Update" : "Save"} loading={loading} className="flex-1 checkout-btn" />
            </div>

            <Dialog header="Select Location" visible={isMapVisible} onHide={() => setIsMapVisible(false)} className="w-full max-w-lg">
                <GetAddressByLocation 
                    getLatitudeLongitude={(latlng: string) => { formik.setFieldValue("gpsLocation", latlng); setIsMapVisible(false); }}
                    lat={formik.values.gpsLocation ? Number(formik.values.gpsLocation.split(',')[0]) : null}
                    lng={formik.values.gpsLocation ? Number(formik.values.gpsLocation.split(',')[1]) : null}
                />
            </Dialog>
        </form>
    );
};

// --- Main Address Component ---
interface SidecartAddressProps {
    onBack: () => void;
    onNext: () => void;
    onClose: () => void;
}

const SidecartAddress: React.FC<SidecartAddressProps> = ({ onBack, onNext, onClose }) => {
    const delivery = useCustomerDelivery();
    const dispatch = useAppDispatch();
    const toast = useRef<Toast>(null);
    const [addresses, setAddresses] = useState<UserAddress[]>([]);
    const [selectedId, setSelectedId] = useState<number>();
    const [view, setView] = useState<'LIST' | 'FORM'>('LIST');
    const [editingId, setEditingId] = useState<number>();
    const [loading, setLoading] = useState(false);

    const selectedAdminUserId = useAppSelector(state => state.adminUser.selectedAdminUserId);
    const [checkingStock, setCheckingStock] = useState(false);
    const [stockError, setStockError] = useState<string | null>(null);
    const checkSequence = useRef(0);
    const loadSequence = useRef(0);
    const checkInFlight = useRef(false);
    const preferredAddressId = selectedAdminUserId ? null : delivery.data?.selectedAddressId;
    const userId = selectedAdminUserId || 
                   JSON.parse(localStorage.getItem("user") || "{}").userID;
    const currentChoice = useRef({ userId, selectedId });
    if (currentChoice.current.userId !== userId || currentChoice.current.selectedId !== selectedId)
        currentChoice.current = { userId, selectedId };
    useEffect(() => { setStockError(null); }, [userId, selectedId]);
    useEffect(() => () => { ++checkSequence.current; ++loadSequence.current; }, []);

    const loadAddresses = useCallback(async () => {
        const version = ++loadSequence.current;
        const owner = userId;
        setLoading(true);
        setAddresses([]);
        setSelectedId(undefined);
        try {
            const list: any = selectedAdminUserId
                ? await UserAddressService.getUserAddressById(selectedAdminUserId)
                : await UserAddressService.getUserAddress(0);
            if (version !== loadSequence.current || currentChoice.current.userId !== owner) return;
            if (Array.isArray(list)) {
                const filtered = list.filter(a => !a.isDeleted);
                setAddresses(filtered);
                const stored = localStorage.getItem("selectedAddressId");
                const preferred = preferredAddressId;
                if (preferred && filtered.some(a => a.userAddressID === preferred)) {
                    setSelectedId(preferred);
                } else if (stored && filtered.some(a => a.userAddressID === Number(stored))) {
                    setSelectedId(Number(stored));
                } else if (filtered.length > 0) {
                    setSelectedId(filtered[0].userAddressID);
                }
            }
        } catch {
            if (version === loadSequence.current && currentChoice.current.userId === owner)
                setStockError('Could not load delivery addresses. Return to the cart and retry.');
        } finally {
            if (version === loadSequence.current && currentChoice.current.userId === owner) setLoading(false);
        }
    }, [selectedAdminUserId, preferredAddressId, userId]);

    useEffect(() => { loadAddresses(); }, [loadAddresses]);

    const handleContinue = async () => {
        if (loading || checkInFlight.current) return;
        const addr = addresses.find(a => a.userAddressID === selectedId);
        if (addr) {
            const version = ++checkSequence.current;
            const owner = userId;
            const choice = currentChoice.current;
            checkInFlight.current = true;
            setCheckingStock(true);
            setStockError(null);
            try {
                await checkCurrentCheckoutDelivery(addr.userAddressID, selectedAdminUserId || undefined);
                if (version !== checkSequence.current || currentChoice.current !== choice || currentChoice.current.userId !== owner || currentChoice.current.selectedId !== addr.userAddressID) return;
                dispatch(selectUserAddress(addr));
                localStorage.setItem("selectedAddressId", addr.userAddressID.toString());
                onNext();
            } catch (error) {
                if (version === checkSequence.current && currentChoice.current === choice) setStockError(error instanceof Error ? error.message : 'Could not verify delivery stock. Please retry.');
            } finally {
                checkInFlight.current = false;
                if (version === checkSequence.current) setCheckingStock(false);
            }
        }
    };

    const handleDelete = async (id: number) => {
        confirmDialog({
            message: 'Are you sure you want to delete this address?',
            header: 'Delete Confirmation',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                const res = await UserAddressService.deleteUserAddress(id);
                if (res.isSuccess) {
                    loadAddresses();
                    toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Address deleted successfully' });
                } else {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: res.message || 'Failed to delete address' });
                }
            }
        });
    };

    return (
        <div className="flex flex-column h-full">
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="side-cart-header">
                {view === 'FORM' ? (
                    <h5><i className="pi pi-plus" /> {editingId ? 'Edit Address' : 'New Address'}</h5>
                ) : (
                    <h5><i className="pi pi-map-marker" /> Shipping Address</h5>
                )}
                <div className="close-icon" onClick={view === 'FORM' ? () => setView('LIST') : onClose}>
                    <i className={view === 'FORM' ? 'pi pi-times' : 'pi pi-arrow-left'} />
                </div>
            </div>

            <div className="side-cart-content text-sm">
                {view === 'LIST' ? (
                    <div className="animate-in">
                        <span className="grey-label mb-3">Select Shipping Address</span>
                        {loading ? (
                            <div className="flex justify-content-center py-8">
                                <i className="pi pi-spin pi-spinner text-4xl text-primary" />
                            </div>
                        ) : (
                            <>
                                {addresses.map(item => (
                                    <div key={item.userAddressID} className={`address-card ${selectedId === item.userAddressID ? 'selected' : ''}`} onClick={() => { if (!checkingStock) setSelectedId(item.userAddressID); }}>
                                        <div className="address-top">
                                            <span className="name">{item.userName}</span>
                                            <div className="flex gap-2">
                                                <Button icon="pi pi-pencil" disabled={checkingStock} className="p-button-text p-button-sm p-button-success" onClick={(e) => { e.stopPropagation(); setEditingId(item.userAddressID); setView('FORM'); }} />
                                                <Button icon="pi pi-trash" disabled={checkingStock} className="p-button-text p-button-sm p-button-danger" onClick={(e) => { e.stopPropagation(); handleDelete(item.userAddressID); }} />
                                            </div>
                                        </div>
                                        <div className="address-details">
                                            <div className="text-gray-900 font-medium mb-1">{item.address1}</div>
                                            <div className="text-gray-600">{item.areaName ? `${item.areaName}, ` : ''}{item.cityName}</div>
                                            <div className="text-gray-600">{item.stateName} - {item.zipcode}</div>
                                        </div>
                                        {selectedId === item.userAddressID && (
                                            <div style={{ position: 'absolute', bottom: '1.25rem', right: '1.25rem' }}>
                                                <i className="pi pi-check-circle text-2xl text-green-500" />
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <div className="add-address-card" onClick={() => { if (!checkingStock) { setEditingId(undefined); setView('FORM'); } }}>
                                    <i className="pi pi-plus-circle text-xl" />
                                    <span className="font-bold">Add New Address</span>
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <AddressForm 
                        addressId={editingId} 
                        onSuccess={() => { setView('LIST'); loadAddresses(); }} 
                        onCancel={() => setView('LIST')} 
                    />
                )}
            </div>

            {view === 'LIST' && (
                <div className="drawer-footer" style={{ flexWrap: 'wrap' }}>
                    {stockError && <p role="alert" className="p-error w-full">{stockError}</p>}
                    <Button label="Back" outlined severity="secondary" className="btn-back" onClick={onBack} />
                    <Button label={checkingStock ? 'Checking stock' : 'Delivery Here'} loading={checkingStock} icon="pi pi-check" iconPos="right" disabled={!selectedId || loading || checkingStock} className="btn-continue checkout-btn" onClick={handleContinue} />
                </div>
            )}
        </div>
    );
};

export default SidecartAddress;
